import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Loader2,
    Globe,
    FileText,
    Download,
    Sparkles,
    CheckCircle2,
    RefreshCw,
    Eye,
    FileDown,
    LayoutTemplate,
    AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { rayicIstihbarat } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { buildRayicRaporPayload, hasRayicRaporVerisi, tl } from "@/components/rayic/rayicUtils";

function tlLocal(n) {
    if (n == null || n === "") return "—";
    return `₺${Number(n).toLocaleString("tr-TR", { maximumFractionDigits: 0 })}`;
}

export function DerinIstihbaratPanel({ dosyaId, onSonuc, onRaporHazir }) {
    const [busy, setBusy] = useState(false);
    const [sonuc, setSonuc] = useState(null);
    const [adim, setAdim] = useState(0);
    const baslat = async () => {
        if (!dosyaId) {
            toast.error("Önce müvekkil / dosya seçin");
            return;
        }
        setBusy(true);
        setAdim(1);
        try {
            setAdim(2);
            const data = await rayicIstihbarat.derinArastir({
                dosya_id: Number(dosyaId),
                arac_piyasa: 1,
                parca_tarama: 1,
            });
            setSonuc(data);
            setAdim(3);
            onSonuc?.(data);
            onRaporHazir?.(data);
            toast.success("Derin istihbarat tamamlandı — rapor hazırlanıyor");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Araştırma başarısız");
            setAdim(0);
        } finally {
            setBusy(false);
        }
    };
    const kaynaklar = sonuc?.internet_tarama?.kaynak_sayilari || {};
    const moduller = sonuc?.moduller || [];
    return (
        <div className="space-y-5">
            <div className="rounded-2xl border border-cyan-500/25 bg-gradient-to-br from-cyan-950/40 via-[#0B0F19] to-[#121A2F] p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <h3 className="font-display text-lg text-white flex items-center gap-2">
                            <Globe className="h-5 w-5 text-cyan-400" />
                            Derin İnternet İstihbaratı
                        </h3>
                        <p className="text-sm text-slate-400 mt-1">
                            Arabam.com, N11.com ve web araması ile çok modüllü paralel tarama.
                        </p>
                    </div>
                    <Button
                        onClick={baslat}
                        disabled={busy || !dosyaId}
                        className="bg-cyan-500 text-black hover:bg-cyan-400 font-semibold"
                    >
                        {busy ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                            <Sparkles className="h-4 w-4 mr-2" />
                        )}
                        Derin Tarama Başlat
                    </Button>
                </div>
                <div className="flex flex-wrap gap-3 mt-5">
                    {["Dosya", "Araç piyasa", "Parça fiyat", "Özet"].map((label, i) => (
                        <div
                            key={label}
                            className={`text-xs px-3 py-1.5 rounded-full border ${
                                adim > i
                                    ? "border-cyan-500/50 text-cyan-300 bg-cyan-500/10"
                                    : "border-white/10 text-slate-500"
                            }`}
                        >
                            {adim > i ? <CheckCircle2 className="h-3.5 w-3.5 inline mr-1" /> : null}
                            {label}
                        </div>
                    ))}
                </div>
            </div>
            {moduller.length ? (
                <div className="grid sm:grid-cols-2 gap-3">
                    {moduller.map((m) => (
                        <div
                            key={m.id}
                            className={`rounded-xl border p-4 ${
                                m.durum === "tamam"
                                    ? "border-emerald-500/30 bg-emerald-500/5"
                                    : "border-rose-500/30 bg-rose-500/5"
                            }`}
                        >
                            <div className="text-white text-sm font-medium">{m.baslik}</div>
                            <div className="text-xs text-slate-400 mt-1">{m.ozet}</div>
                        </div>
                    ))}
                </div>
            ) : null}
            {Object.keys(kaynaklar).length ? (
                <div className="rounded-xl border border-white/10 p-4 flex flex-wrap gap-2">
                    {Object.entries(kaynaklar).map(([k, v]) => (
                        <span
                            key={k}
                            className="text-xs px-3 py-1.5 rounded-lg bg-white/5 border border-white/10"
                        >
                            {k}: <strong className="text-cyan-300">{v}</strong>
                        </span>
                    ))}
                </div>
            ) : null}
        </div>
    );
}

const TEMPLATES = [
    { id: "ultra", label: "Ultra Gelişmiş", desc: "Kapak + risk skoru + grafik" },
    { id: "kurumsal", label: "Kurumsal", desc: "Klasik MBS logolu rapor" },
];

export function RaporMerkeziPanel({
    dosyaId,
    derinSonuc,
    parcaSonuc,
    parcaHasar,
    sonuc,
    initialHtml = "",
    externalBusy = false,
    onOlustur,
    autoTrigger = 0,
}) {
    const [html, setHtml] = useState(initialHtml || "");
    const [busy, setBusy] = useState(false);
    const [pdfBusy, setPdfBusy] = useState(false);
    const [template, setTemplate] = useState("ultra");
    const lastTrigger = useRef(0);

    useEffect(() => {
        if (initialHtml) setHtml(initialHtml);
    }, [initialHtml]);

    const hazir = hasRayicRaporVerisi({ parcaSonuc, derinSonuc, dosyaId, parcaHasar });

    const payload = useCallback(
        () =>
            buildRayicRaporPayload({
                dosyaId,
                derinSonuc,
                parcaSonuc,
                parcaHasar,
                sonuc,
                template,
            }),
        [dosyaId, derinSonuc, parcaSonuc, parcaHasar, sonuc, template],
    );

    const onizle = useCallback(
        async (silent = false) => {
            if (onOlustur) {
                const out = await onOlustur(null, { tabAc: false, silent });
                if (out) setHtml(out);
                return;
            }
            if (!hazir) {
                if (!silent) toast.error("Önce parça analizi tamamlayın");
                return;
            }
            setBusy(true);
            try {
                const res = await rayicIstihbarat.raporOnizle(payload());
                setHtml(res.html || "");
                if (!silent) toast.success("Gelişmiş rapor önizlemesi hazır");
            } catch (e) {
                if (!silent) toast.error(e?.response?.data?.detail || e?.message || "Önizleme oluşturulamadı");
            } finally {
                setBusy(false);
            }
        },
        [hazir, payload, onOlustur],
    );

    const pdfIndir = async () => {
        if (!hazir && !html) {
            toast.error("Önce parça analizi tamamlayın");
            return;
        }
        setPdfBusy(true);
        try {
            const blob = await rayicIstihbarat.raporPdf(payload());
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            const arac = parcaSonuc?.arac || derinSonuc?.arac || {};
            const ad = arac.dosya_no || arac.plaka || dosyaId || "rapor";
            a.href = url;
            a.download = `rayic-${template}-${ad}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("PDF indirildi");
        } catch (e) {
            toast.error(e?.response?.data?.detail || e?.message || "PDF indirilemedi");
        } finally {
            setPdfBusy(false);
        }
    };

    useEffect(() => {
        if (autoTrigger > 0 && autoTrigger !== lastTrigger.current && initialHtml) {
            lastTrigger.current = autoTrigger;
            setHtml(initialHtml);
        }
    }, [autoTrigger, initialHtml]);

    const isBusy = busy || externalBusy;

    const oz = parcaSonuc?.parca_arastirma?.ozet;
    const hf = parcaHasar || parcaSonuc?.hasar_farki;

    return (
        <div className="space-y-4">
            {hazir ? (
                <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 flex flex-wrap items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-emerald-200">Analiz tamamlandı — rapor hazır</div>
                        <div className="text-xs text-slate-400 mt-0.5">
                            {oz
                                ? `${oz.parca_sayisi} parça · ${oz.yuksek_sapma_parca || 0} yüksek sapma · Rapor: ${tlLocal(oz.toplam_rapor_tutar)}`
                                : hf?.karar
                                  ? `Karar: ${hf.karar.replace(/_/g, " ")}`
                                  : "Veriler rapora aktarılacak"}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="rounded-xl border border-amber-500/25 bg-amber-500/5 p-4 flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-100/90">
                        Ekspertiz yükleyip parça fiyat taraması tamamlayın — analiz bitince rapor otomatik
                        oluşturulur.
                    </div>
                </div>
            )}

            <div className="rounded-xl border border-white/10 bg-[#0B0F19]/60 p-4 space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                    <LayoutTemplate className="h-4 w-4 text-[#D4AF37]" />
                    <span className="text-xs uppercase tracking-wider text-slate-500">Rapor şablonu</span>
                </div>
                <div className="grid sm:grid-cols-2 gap-2">
                    {TEMPLATES.map((t) => (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => setTemplate(t.id)}
                            className={`text-left rounded-lg border p-3 transition ${
                                template === t.id
                                    ? "border-[#D4AF37]/60 bg-[#D4AF37]/10"
                                    : "border-white/10 hover:border-white/20"
                            }`}
                        >
                            <div className="text-sm font-medium text-white">{t.label}</div>
                            <div className="text-[10px] text-slate-500 mt-0.5">{t.desc}</div>
                        </button>
                    ))}
                </div>
            </div>

            <div className="flex flex-wrap gap-2">
                <Button
                    onClick={() => onizle(false)}
                    disabled={isBusy || !hazir}
                    className="bg-[#D4AF37] text-black hover:bg-[#e5c04a] font-semibold"
                >
                    {isBusy ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                        <Eye className="h-4 w-4 mr-2" />
                    )}
                    Gelişmiş Rapor Önizle
                </Button>
                <Button
                    variant="outline"
                    onClick={pdfIndir}
                    disabled={pdfBusy || !hazir}
                    className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                >
                    {pdfBusy ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                        <Download className="h-4 w-4 mr-2" />
                    )}
                    PDF İndir
                </Button>
                <Button variant="ghost" onClick={() => onizle(false)} disabled={!hazir} className="text-slate-400">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Yenile
                </Button>
            </div>

            {html ? (
                <div className="rounded-2xl border-2 border-[#D4AF37]/30 overflow-hidden shadow-2xl">
                    <div className="bg-[#0f172a] px-4 py-2 text-xs text-slate-400 border-b border-white/10 flex items-center justify-between">
                        <span className="flex items-center gap-2">
                            <FileText className="h-3.5 w-3.5 text-[#D4AF37]" />
                            {template === "ultra" ? "Ultra Gelişmiş Rapor" : "Kurumsal Rapor"} — MBS logolu
                        </span>
                        <button
                            type="button"
                            onClick={pdfIndir}
                            disabled={pdfBusy}
                            className="inline-flex items-center gap-1 text-[#D4AF37] hover:text-[#F3E5AB]"
                        >
                            <FileDown className="h-3.5 w-3.5" />
                            PDF
                        </button>
                    </div>
                    <iframe
                        title="Rapor"
                        srcDoc={html}
                        className="w-full bg-white"
                        style={{ minHeight: "820px", border: "none" }}
                    />
                </div>
            ) : hazir ? (
                <div className="rounded-xl border border-dashed border-[#D4AF37]/30 py-12 text-center">
                    <FileText className="h-10 w-10 mx-auto mb-3 text-[#D4AF37]/50" />
                    <p className="text-sm text-slate-400">Önizleme yükleniyor veya &quot;Gelişmiş Rapor Önizle&quot;ye tıklayın</p>
                </div>
            ) : (
                <div className="rounded-xl border border-dashed border-white/15 py-16 text-center text-slate-500 text-sm">
                    <FileText className="h-10 w-10 mx-auto mb-3 opacity-40" />
                    Parça analizi tamamlandığında ultra gelişmiş PDF raporu burada görünecek.
                </div>
            )}
        </div>
    );
}

/** Parça sekmesinde analiz sonrası hızlı rapor CTA */
export function RayicRaporHizliBar({ parcaSonuc, parcaHasar, onRaporTab, onPdfIndir, pdfBusy }) {
    const oz = parcaSonuc?.parca_arastirma?.ozet;
    const hf = parcaHasar || parcaSonuc?.hasar_farki;
    if (!oz && !hf) return null;

    const kritik = hf?.karar === "hasar_farki_kritik";
    return (
        <div
            className={`rounded-xl border p-4 flex flex-wrap items-center gap-3 ${
                kritik ? "border-rose-500/40 bg-rose-500/10" : "border-emerald-500/30 bg-emerald-500/5"
            }`}
        >
            <Sparkles className={`h-5 w-5 shrink-0 ${kritik ? "text-rose-400" : "text-emerald-400"}`} />
            <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-white">Analiz tamam — Gelişmiş PDF rapor hazır</div>
                <div className="text-xs text-slate-400 mt-0.5">
                    {oz
                        ? `${oz.parca_sayisi} parça tarandı · Orijinal ${tl(oz.toplam_orijinal_medyan || oz.toplam_orijinal_oneri)} · Muadil ${tl(oz.toplam_muadil_medyan || oz.toplam_muadil_oneri)}`
                        : "Hasar farkı değerlendirmesi tamamlandı"}
                </div>
            </div>
            <div className="flex flex-wrap gap-2">
                <Button
                    size="sm"
                    onClick={onRaporTab}
                    className="bg-[#D4AF37] text-black hover:bg-[#e5c04a] font-semibold"
                >
                    <FileText className="h-3.5 w-3.5 mr-1.5" />
                    Raporu Gör
                </Button>
                <Button
                    size="sm"
                    variant="outline"
                    onClick={onPdfIndir}
                    disabled={pdfBusy}
                    className="border-white/15"
                >
                    {pdfBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}
                    PDF
                </Button>
            </div>
        </div>
    );
}
